import { groupBy } from "~/utils/tsUtils"

type TableUpdateRun = { nRows: number; startTime: Date; endTime: Date | null }

// The loader's error path writes `nRows = -1` and still stamps `endTime`, so a failed table
// load is otherwise indistinguishable from a fast successful one (update_db/insert_data.py).
export function isFailedTableUpdate(run: { nRows: number }): boolean {
  return run.nRows < 0
}

export function tableUpdateStatus(run: { nRows: number; isCached: boolean }): {
  label: string
  variant: "red" | "green" | "blue"
  tooltip: string
} {
  if (isFailedTableUpdate(run)) {
    return {
      label: "Failed",
      variant: "red",
      tooltip: "Load failed. Row count and delta are not reported for this run",
    }
  }
  if (run.isCached) {
    return {
      label: "No update",
      variant: "green",
      tooltip: "Rows verified identical to previous ingestion, so no transfer needed",
    }
  }
  return {
    label: "Updated",
    variant: "blue",
    tooltip: "Data transferred. Row count or values changed since last ingestion",
  }
}

// Chained across successful runs only: the -1 sentinel would otherwise read as a huge drop on
// the failed run and an equally huge rise on the next successful one.
export function rowDeltaByRun<T extends TableUpdateRun>(
  runs: T[] | null | undefined,
  tableKeyOf: (run: T) => string,
  runKeyOf: (run: T) => string,
): Map<string, number | null> {
  const deltas = new Map<string, number | null>()
  const succeeded = (runs ?? []).filter((r) => r.endTime != null && !isFailedTableUpdate(r))

  for (const table of Object.values(groupBy(succeeded, tableKeyOf))) {
    const newestFirst = [...table].sort((a, b) => b.startTime.getTime() - a.startTime.getTime())
    for (let i = 0; i < newestFirst.length; i++) {
      const current = newestFirst[i]!
      const prev = newestFirst[i + 1]
      deltas.set(runKeyOf(current), prev != null ? current.nRows - prev.nRows : null)
    }
  }

  return deltas
}
